import { advanceLogoutChain, beginLogoutChain, consumeLogoutReturn, isLogoutChainActive } from './services/LogoutChain';
import './App.css';
import AppRoutes from './routes';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Toast from './services/Toast';
import axiosInstance from './Axios';
import { isLogedIn, redirectToApplication } from './services/AuthService';
import { getPendingApplication, withApplication } from './services/PendingApplication';
import Loader from './components/loader/Loader';
import images from './assets/images';
import Footer from './components/footer/Footer';

const App: React.FC = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const currentPath = location.pathname;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const removeLocalUserInfo = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };
  const updateUserInfo = async (errorMessage = null) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/auth/me');
      setLoading(false);
      if (!response.data.status) {
        removeLocalUserInfo();
        return;
      }

      localStorage.setItem('user', JSON.stringify(response.data.data));
      if (errorMessage){
        Toast.error(errorMessage);
      }
      return ;
    } catch (error: any) {
      setLoading(false);
      removeLocalUserInfo();
      navigate(withApplication('/'));
    }

  };
  const managePreloadLogic = async () => {
    const errorMessage:any = searchParams.get('error_message');
    const action = searchParams.get('action');
    // Logout is handled BEFORE the application handoff below. linentech echoes
    // the app back on its logout return, so that URL is ?action=logout&
    // application=linentech — and the handoff branch would happily hand a still
    // -valid session straight back to the app instead of ending it (reachable by
    // navigating back to that URL after signing in again). An explicit logout
    // instruction always wins.
    if (action === 'logout') {
      // Device-scoped logout handoff: a client app (e.g. linentech) redirected
      // the browser here on its own logout. Only this browser's SSO session
      // ends — the same user's sessions on other devices stay alive. The bearer
      // token added by the request interceptor is what identifies this device.
      try {
        setLoading(true);
        // Skip the call with no token: it would 401 and, since we land here
        // with ?action=logout in the URL, bounce straight back into this branch.
        if (isLogedIn()) {
          await axiosInstance.get("/auth/logout");
          Toast.success("Signed out successfully.");
        }
      }
      catch (e)
      {
        // A failure here means the session is already gone — nothing to undo.
      }
      finally
      {
        removeLocalUserInfo();

        // An app has just ended its own session and handed the browser back.
        // Either we are part-way through a chain the portal started, or this app
        // started the logout itself and the other applications still hold a live
        // session — either way, keep going until none are left. The queue shrinks
        // on every hop, so this always terminates.
        const application = searchParams.get('application');
        const stillGoing = isLogoutChainActive()
          ? advanceLogoutChain(application)
          : beginLogoutChain(application, application, searchParams.get('return_to'));

        if (stillGoing) {
          // Navigating away; leave the spinner up rather than flashing the login
          // page between hops.
          return;
        }

        // The chain is done. A product that asked to be returned to gets the
        // browser back — linentech's own logout has always ended on its home
        // page, and routing it through here must not change that.
        const returnTo = consumeLogoutReturn();
        if (returnTo) {
          window.location.replace(returnTo);
          return;
        }

        // Land on the login page with ?action=logout dropped so a reload or
        // the back button can't replay it, but keep ?application=<app> when
        // present. That param is what makes the login form redirect the user
        // back to the originating app after they sign in again — stripping
        // the full query string here breaks that handoff.
        setLoading(false);
        navigate(withApplication('/'), { replace: true });
      }
      return;
    }
    // Read the app from storage, not just the URL: a route guard may already
    // have redirected /?application=x to /home and stripped the param before
    // this effect runs.
    const pendingApplication = getPendingApplication();
    if (pendingApplication && isLogedIn() && currentPath !== '/switch') {
      setLoading(true);
      if(! await redirectToApplication(pendingApplication)){
        setLoading(false);
      }

      return;
    }
    // On /switch, OTT exchange is the auth step — do not refresh session or send users to /home
    // (same rationale as skipping application redirect above).
    if (isLogedIn() && currentPath !== '/switch') {
      updateUserInfo(errorMessage);
    }
    if (errorMessage && currentPath !== '/switch') {
      Toast.error(errorMessage);
      navigate('/home', { replace: true });
      return;
    }
  };
  useEffect(() => {
    managePreloadLogic();
  }, []);
  if (loading) {
    return (
      <div className="align-items-center d-flex">
        <Loader />
      </div>
    );
  }
  return (
    <div className={`App ${currentPath === '/' ? '':'homeApp'}`}>
      <div className={`w-100 position-relative ${currentPath === '/' ? 'h-100':''}`}>
        <div className='h-100 centeredContent'>
          <AppRoutes />
          <ToastContainer />
        </div>
        {currentPath !== '/' && <Footer currentPath={currentPath}/>}
      </div>
    </div>
  );
};

export default App;
